import { Fragment } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { plNumber } from '../../shared/number';
import {
  ROUTE_SOURCE_LABELS,
  driveText,
  isMeasured,
  kmText,
  routeFigureTooltip,
  routeSummaryText,
  type RouteFigure,
} from './routeFigure';

/**
 * Dolot i trasa zlecenia na ekranie — wspólne dla obu kierunków rekomendacji
 * (zlecenie → pojazdy i pojazd → zlecenia), żeby obie listy mówiły to samo.
 * Reguły treści: `routeFigure.ts`.
 */

const NOWRAP = { whiteSpace: 'nowrap' } as const;

/** Dolot ze starszego API — linia prosta razy mnożnik, bez źródła. */
export interface LegacyAccessDistance {
  straightKm: number;
  roadKm: number;
  multiplier: number;
}

/**
 * Ikonka „i” z dymkiem. Dymek nadaje jej nazwę (`aria-label`), a `role="img"`
 * i `tabIndex` pozwalają dojść do niej klawiaturą i czytnikiem ekranu — ten
 * sam wzór co przy nagłówku „Zapas”.
 */
export function InfoHint({ title, testId }: { title: string; testId?: string }) {
  return (
    <Tooltip title={title}>
      <Box
        component="span"
        role="img"
        tabIndex={0}
        data-testid={testId}
        sx={{ display: 'inline-flex', cursor: 'help', color: 'text.secondary', ml: 0.25 }}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </Box>
    </Tooltip>
  );
}

/**
 * Komórka dolotu.
 *
 * - Starsze API (bez `route`) — dokładnie jak dotąd: liczba z rachunkiem
 *   w dymku, „szacunek” mówi nagłówek kolumny.
 * - Szacunek (`estimate`, HERE wyłączone albo bez odpowiedzi) — wygląd jak
 *   dotąd, w dymku rachunek i powód z API. Podpis „szacunek” w komórce tylko
 *   wtedy, gdy tabela ma też liczby z map (`showSource`) — wtedy nagłówek
 *   jest neutralny i bez podpisu nie dałoby się ich odróżnić.
 * - Trasa z map (`here`) — liczba, obok „i” z dymkiem poglądowym (linia
 *   prosta × mnożnik, czas z modelu floty), pod nią źródło i czas jazdy
 *   z map. Czas pod liczbą, nie w dymku: planista porównuje go między autami,
 *   a dymek jest na rachunek poglądowy.
 *
 * `wrapSource` — podpis „HERE · jazda ok. 14,2 h” może złamać się po „·”
 * (karta pojazdu, 06.10.2026: w jednej linii wyznaczał szerokość kolumny,
 * a tabela nie mieściła się w oknie 1366 px). Bez niego — jedna linia, jak
 * w rekomendacjach pojazdów.
 */
export function AccessDistanceCell({
  route,
  legacy,
  showSource,
  wrapSource = false,
}: {
  route?: RouteFigure | null;
  legacy: LegacyAccessDistance;
  showSource: boolean;
  wrapSource?: boolean;
}) {
  if (!route) {
    return (
      <Tooltip
        title={`Dolot: ${plNumber(legacy.straightKm)} km w linii prostej × mnożnik terenowy ${plNumber(legacy.multiplier, 3)}`}
      >
        <Typography variant="body2" sx={NOWRAP} data-testid="access-km">
          {kmText(legacy.roadKm)}
        </Typography>
      </Tooltip>
    );
  }
  if (!isMeasured(route)) {
    return (
      <>
        <Tooltip title={routeFigureTooltip(route)}>
          <Typography variant="body2" sx={NOWRAP} data-testid="access-km">
            {kmText(route.km ?? route.estimate.km)}
          </Typography>
        </Tooltip>
        {showSource && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', ...NOWRAP }}
            data-testid="access-source"
          >
            {ROUTE_SOURCE_LABELS.estimate}
          </Typography>
        )}
      </>
    );
  }
  const drive = driveText(route);
  const source = [ROUTE_SOURCE_LABELS[route.source], drive].filter((s): s is string => Boolean(s));
  return (
    <>
      <Typography
        variant="body2"
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', ...NOWRAP }}
      >
        <span data-testid="access-km">{kmText(route.km)}</span>
        <InfoHint title={routeFigureTooltip(route)} testId="access-preview" />
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={wrapSource ? { display: 'block' } : { display: 'block', ...NOWRAP }}
        data-testid="access-source"
      >
        {wrapSource
          ? source.map((part, i) => (
              <Fragment key={part}>
                {i > 0 ? ' ' : null}
                <Box component="span" sx={NOWRAP} data-testid="access-source-part">
                  {i < source.length - 1 ? `${part} ·` : part}
                </Box>
              </Fragment>
            ))
          : source.join(' · ')}
      </Typography>
    </>
  );
}

/**
 * Kawałki zwrotu o trasie, między którymi wolno złamać linię: po przecinku,
 * przed „wg …” i przed nawiasem — „trasa 1418,3 km” / „wg HERE,” / „jazda
 * ok. 16,1 h”, „trasa ok. 655 km” / „(szacunek)”.
 */
export function compactRouteParts(text: string): string[] {
  return text
    .split(', ')
    .map((part, i, all) => (i < all.length - 1 ? `${part},` : part))
    .flatMap((part) => part.split(/ (?=wg |\()/));
}

/**
 * Trasa zlecenia jednym zwrotem z „i” obok: „Trasa zlecenia: 812 km wg HERE,
 * jazda ok. 10,1 h”. `compact` — druga linia w komórce „Relacja” (pojazd →
 * zlecenia); bez — linia nad tabelą rekomendacji pojazdów. Brak trasy (starsze
 * API albo `null`) — nic.
 *
 * W `compact` zwrot łamie się tylko między kawałkami (`compactRouteParts`),
 * nigdy w środku „1418,3 km” czy „jazda ok. 16,1 h” (06.10.2026): w jednej
 * linii poszerzał kolumnę „Relacja” do ok. 280 px i tabela na karcie pojazdu
 * nie mieściła się w oknie 1366 px.
 */
export function OrderRouteNote({ route, compact = false }: { route?: RouteFigure | null; compact?: boolean }) {
  if (!route) return null;
  const hint = <InfoHint title={routeFigureTooltip(route)} testId="order-route-preview" />;
  if (compact) {
    const parts = compactRouteParts(`trasa ${routeSummaryText(route)}`);
    return (
      <Typography variant="caption" color="text.secondary" data-testid="order-route" sx={{ display: 'block' }}>
        {parts.map((part, i) => (
          <Fragment key={part}>
            {i > 0 ? ' ' : null}
            <Box component="span" sx={NOWRAP} data-testid="order-route-part">
              {part}
              {i === parts.length - 1 ? hint : null}
            </Box>
          </Fragment>
        ))}
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      data-testid="order-route"
      sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
    >
      {`Trasa zlecenia: ${routeSummaryText(route)}`}
      {hint}
    </Typography>
  );
}
